import { useCallback, useEffect, useRef, useState } from 'react';
import resolveApiBase from '../../tradingCockpit/apiBase';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;
const STATE_POLL_MS = 10000;

const resolveUrl = (path) => {
    if (!API_BASE) return path;
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const buildHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (API_KEY) headers['x-api-key'] = API_KEY;
    return headers;
};

const EMPTY_COUNTS = { deploy: 0, skip: 0, missed: 0 };

/**
 * Recent gate decisions for Vidura — the frozen paper lockbox.
 * GET /api/strategies/vidura/decisions?limit=50
 */
export function useViduraDecisions(enabled = true, limit = 50) {
    const [decisions, setDecisions] = useState([]);
    const [counts, setCounts] = useState(EMPTY_COUNTS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const aliveRef = useRef(true);

    const fetchDecisions = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (limit) params.set('limit', String(limit));
            const resp = await fetch(resolveUrl(`/api/strategies/vidura/decisions?${params}`), {
                method: 'GET',
                headers: buildHeaders(),
                credentials: 'include',
                cache: 'no-store',
            });
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const data = await resp.json();
            if (!aliveRef.current) return;
            setDecisions(Array.isArray(data.decisions) ? data.decisions : []);
            setCounts({ ...EMPTY_COUNTS, ...(data.counts || {}) });
            setError(null);
        } catch (err) {
            if (!aliveRef.current) return;
            setDecisions([]);
            setCounts(EMPTY_COUNTS);
            setError(err.message || String(err));
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [enabled, limit]);

    useEffect(() => {
        aliveRef.current = true;
        fetchDecisions();
        return () => {
            aliveRef.current = false;
        };
    }, [fetchDecisions]);

    return {
        decisions,
        counts,
        loading,
        error,
        refresh: fetchDecisions,
    };
}

/**
 * Full Vidura state snapshot — gate/positions/pnl live at the top level of the
 * payload (the generic strategy monitor only surfaces `summary`, so we fetch the
 * raw contract here for the gate metrics and Gate panel).
 * GET /api/strategies/vidura/state
 */
export function useViduraState(enabled = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const aliveRef = useRef(true);

    const fetchState = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        try {
            const resp = await fetch(resolveUrl('/api/strategies/vidura/state'), {
                method: 'GET',
                headers: buildHeaders(),
                credentials: 'include',
                cache: 'no-store',
            });
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const payload = await resp.json();
            if (!aliveRef.current) return;
            setData(payload);
            setError(null);
        } catch (err) {
            if (!aliveRef.current) return;
            setError(err.message || String(err));
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        aliveRef.current = true;
        if (!enabled) {
            return () => {
                aliveRef.current = false;
            };
        }
        fetchState();
        const handle = window.setInterval(fetchState, STATE_POLL_MS);
        return () => {
            aliveRef.current = false;
            window.clearInterval(handle);
        };
    }, [enabled, fetchState]);

    return {
        data,
        gate: data?.gate || null,
        positions: data?.positions || null,
        pnl: data?.pnl || null,
        status: data?.status || null,
        heartbeat: data?.heartbeat || null,
        asOf: data?.asOf || null,
        loading,
        error,
        refresh: fetchState,
    };
}

export default useViduraDecisions;
